/**
 * useChatOrderHandlers — extracted callbacks from ChatOrderSheet.
 *
 * Contains ONLY useCallback calls. No useState, useEffect, useMemo, or useRef.
 * All state values and setters are passed in via the options object.
 */

import { useCallback } from "react";
import { toast } from "sonner";
import { getERPClient } from "@/lib/erp/client";
import { frappeMethod } from "@/lib/erp/utils";
import type { POSItem } from "@/features/pos/types";
import type { SalesOrderItemCustomization } from "@/lib/erp/types/order";
import type {
  CheckoutLinkPayload,
  AddressForm,
  PreviewResult,
  AppliedCoupon,
  Step,
  EMPTY_ADDRESS,
} from "./chatOrderTypes";

type EmptyAddress = typeof EMPTY_ADDRESS;

interface CartHandle {
  items: Array<{
    item_code: string;
    item_name: string;
    qty: number;
    rate: number;
    amount: number;
    item_image?: string | null;
    line_id?: string;
  }>;
  addItem: (item: POSItem, variantAttrs?: string, lineId?: string) => void;
  removeItem: (itemCode: string, lineId?: string) => void;
  clearCart: () => void;
}

interface Options {
  cart: CartHandle;
  bundleSurchargeMap: Record<string, number>;
  bundleSelectionsMap: Record<string, Record<string, Record<string, string>>>;
  customizationsMap: Record<string, Record<string, string>>;
  address: AddressForm;
  storefrontTenantKey: string | null | undefined;
  conversationId: string | undefined;
  appliedCoupons: AppliedCoupon[];
  couponInput: string;
  preview: PreviewResult | null;
  onCheckoutLinkReady?: (payload: CheckoutLinkPayload) => void;
  onOpenChange: (open: boolean) => void;
  emptyAddress: EmptyAddress;
  // Setters
  setStep: (s: Step) => void;
  setAddress: React.Dispatch<React.SetStateAction<AddressForm>>;
  setCustomizationsMap: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
  setAppliedCoupons: React.Dispatch<React.SetStateAction<AppliedCoupon[]>>;
  setCouponInput: React.Dispatch<React.SetStateAction<string>>;
  setPreview: React.Dispatch<React.SetStateAction<PreviewResult | null>>;
  setPreviewing: React.Dispatch<React.SetStateAction<boolean>>;
  setPreviewError: React.Dispatch<React.SetStateAction<string | null>>;
  setGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  setBundleModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setBundleItemCode: React.Dispatch<React.SetStateAction<string>>;
  setBundleSelectionsMap: React.Dispatch<React.SetStateAction<Record<string, Record<string, Record<string, string>>>>>;
  setBundleSurchargeMap: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  setPersonalizationItemCode: React.Dispatch<React.SetStateAction<string | null>>;
  setPersonalizationItemName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPendingLineId: React.Dispatch<React.SetStateAction<string | null>>;
}

export function useChatOrderHandlers(opts: Options) {
  const {
    cart, bundleSurchargeMap, bundleSelectionsMap, customizationsMap,
    address, storefrontTenantKey, conversationId, appliedCoupons,
    couponInput, preview, onCheckoutLinkReady, onOpenChange, emptyAddress,
    setStep, setAddress, setCustomizationsMap, setAppliedCoupons,
    setCouponInput, setPreview, setPreviewing, setPreviewError,
    setGenerating, setBundleModalOpen, setBundleItemCode,
    setBundleSelectionsMap, setBundleSurchargeMap,
    setPersonalizationItemCode, setPersonalizationItemName, setPendingLineId,
  } = opts;

  // Add item to cart with optional personalization data and line_id (for configurable bundles)
  const addItemToCart = useCallback(
    async (itemCode: string, customizations?: SalesOrderItemCustomization[], bundleLineId?: string) => {
      // A personalized unit gets its own cart row so "1 embroidered + 1 plain"
      // of the same item/size stays two lines instead of merging into qty 2
      // that inherits one personalization (TKT-2026-00150).
      const lineId = bundleLineId ?? (customizations?.length ? crypto.randomUUID() : undefined);
      try {
        const itemDetails = await frappeMethod<{
          item_code: string;
          item_name: string;
          standard_rate: number;
          primary_image: string | null;
        }>("zaviago_backend.api.item_list.get_item_with_primary_image", {
          item_code: itemCode,
        });

        if (itemDetails) {
          // Add variant surcharge (from BundleConfiguratorModal) to base price
          const variantSurcharge = lineId ? (bundleSurchargeMap[lineId] ?? 0) : 0;
          cart.addItem({
            item_code: itemDetails.item_code,
            item_name: itemDetails.item_name,
            price_list_rate: (itemDetails.standard_rate || 0) + variantSurcharge,
            item_image: itemDetails.primary_image || undefined,
          } as POSItem, undefined, lineId);
        }
      } catch {
        cart.addItem({
          item_code: itemCode,
          item_name: itemCode,
          price_list_rate: 0,
        } as POSItem, undefined, lineId);
      }

      // Store personalization keyed by the row's lineId (bundle or personalized unit)
      if (customizations?.length && lineId) {
        const key = lineId;
        const fieldValues: Record<string, string> = {};
        for (const c of customizations) {
          fieldValues[c.field_label] = c.file_url || c.value;
        }
        setCustomizationsMap((prev) => ({ ...prev, [key]: fieldValues }));
      }
      setPreview(null);
    },
    [cart.addItem, bundleSurchargeMap, setCustomizationsMap, setPreview]
  );

  const handleSelectItem = useCallback(
    (itemCode: string, customizations?: SalesOrderItemCustomization[]) => {
      addItemToCart(itemCode, customizations);
    },
    [addItemToCart],
  );

  const handleBundleItemClick = useCallback(
    (_itemCode: string, _itemName: string) => {
      setBundleItemCode(_itemCode);
      setBundleModalOpen(true);
    },
    [setBundleItemCode, setBundleModalOpen]
  );

  const handleBundleConfirm = useCallback(
    (bundleCode: string, selections: Record<string, Record<string, string>>, surcharge: number) => {
      const lineId = crypto.randomUUID();
      setBundleSelectionsMap((prev) => ({ ...prev, [lineId]: selections }));
      if (surcharge > 0) {
        setBundleSurchargeMap((prev) => ({ ...prev, [lineId]: surcharge }));
      }
      setPendingLineId(lineId);
      setPersonalizationItemCode(bundleCode);
      setPersonalizationItemName(undefined);
    },
    [setBundleSelectionsMap, setBundleSurchargeMap, setPendingLineId, setPersonalizationItemCode, setPersonalizationItemName]
  );

  const handleRemoveItem = useCallback(
    (itemCode: string, lineId?: string) => {
      cart.removeItem(itemCode, lineId);
      const key = lineId || itemCode;
      setBundleSelectionsMap((prev) => { const next = { ...prev }; delete next[key]; return next; });
      setCustomizationsMap((prev) => { const next = { ...prev }; delete next[key]; return next; });
      setBundleSurchargeMap((prev) => { const next = { ...prev }; delete next[key]; return next; });
      setPreview(null);
    },
    [cart.removeItem, setBundleSelectionsMap, setCustomizationsMap, setBundleSurchargeMap, setPreview],
  );

  const handleBackToProducts = useCallback(() => {
    setStep("products");
  }, [setStep]);

  const updateAddress = useCallback(
    (field: keyof AddressForm, value: string) => {
      setAddress((prev) => ({ ...prev, [field]: value }));
    },
    [setAddress],
  );

  // Core preview API call
  const callPreview = useCallback(async (couponCodes: string[], newCode?: string) => {
    if (cart.items.length === 0) return;

    setPreviewing(true);
    setPreviewError(null);
    try {
      const client = getERPClient();

      const items = cart.items.map((i) => {
        const key = i.line_id || i.item_code;
        return {
          item_code: i.item_code,
          qty: i.qty,
          ...(bundleSelectionsMap[key] && { bundle_variant_selections: bundleSelectionsMap[key] }),
          ...(customizationsMap[key] && { customizations: customizationsMap[key] }),
        };
      });

      const hasAddress = address.address_title || address.phone;
      const addressPayload = hasAddress ? JSON.stringify(address) : undefined;

      const result = await client.call<PreviewResult>(
        "zaviago_backend.storefront.chat_order.preview_chat_order",
        {
          tenant_key: storefrontTenantKey,
          items: JSON.stringify(items),
          ...(couponCodes.length > 0 && { coupon_codes: JSON.stringify(couponCodes) }),
          ...(addressPayload && { address: addressPayload }),
        },
      );

      if (result) {
        setPreview(result);
        if (result.applied_coupons?.length) setAppliedCoupons(result.applied_coupons);
        if (newCode) {
          const newCoupon = result.applied_coupons?.find((c) => c.code === newCode);
          if (newCoupon?.valid) { setCouponInput(""); toast.success(`Coupon "${newCode}" applied`); }
          else if (newCoupon && !newCoupon.valid) setPreviewError(newCoupon.message ?? "Invalid coupon");
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to preview order";
      setPreviewError(msg);
    } finally {
      setPreviewing(false);
    }
  }, [cart.items, storefrontTenantKey, bundleSelectionsMap, customizationsMap, address,
      setPreviewing, setPreviewError, setPreview, setAppliedCoupons, setCouponInput]);

  const handlePreview = useCallback(async () => {
    const allCodes = [...appliedCoupons.filter((c) => c.valid).map((c) => c.code)];
    const newCode = couponInput.trim();
    if (newCode && !allCodes.includes(newCode)) allCodes.push(newCode);
    await callPreview(allCodes, newCode);
  }, [callPreview, couponInput, appliedCoupons]);

  const handleRemoveCoupon = useCallback((code: string) => {
    const remaining = appliedCoupons.filter((c) => c.code !== code && c.valid).map((c) => c.code);
    setAppliedCoupons((prev) => prev.filter((c) => c.code !== code));
    if (remaining.length > 0) {
      callPreview(remaining);
    } else {
      callPreview([]);
    }
  }, [appliedCoupons, callPreview, setAppliedCoupons]);

  // Generate link: finalize the previewed Quotation
  const handleGenerateLink = useCallback(async () => {
    if (!preview) return;

    setGenerating(true);
    try {
      const client = getERPClient();

      const hasAddress = address.address_title || address.phone;
      const addressPayload = hasAddress ? JSON.stringify(address) : undefined;

      const result = await client.call<{ url: string; grand_total: number }>(
        "zaviago_backend.storefront.chat_order.confirm_chat_order",
        {
          tenant_key: storefrontTenantKey,
          quotation_name: preview.quotation_name,
          conversation_id: conversationId,
          ...(addressPayload && { address: addressPayload }),
        },
      );

      const url = result?.url;
      // Use confirm's grand_total — it reflects the FINAL quotation state
      // (address/shipping may have been attached during confirm, after preview).
      const finalTotal = result?.grand_total ?? preview.grand_total;
      if (url) {
        onCheckoutLinkReady?.({
          url,
          grand_total: finalTotal,
          subtotal: preview.subtotal,
          currency: preview.currency,
          shipping: preview.shipping_amount,
          products: (() => {
            const nonAddonItems = preview.items.filter((i) => !i.is_addon);
            return nonAddonItems.map((i) => {
              // Preview rows are consolidated per item_code — match, don't index
              const cartItem = cart.items.find((c) => c.item_code === i.item_code);
              return {
                item_code: i.item_code,
                item_name: i.item_name,
                qty: i.qty,
                rate: i.rate,
                price: i.rate,
                currency: preview.currency,
                image_url: cartItem?.item_image ?? undefined,
              };
            });
          })(),
          customer: address.address_title
            ? { name: address.address_title, phone: address.phone }
            : undefined,
        });
        cart.clearCart();
        setAddress(emptyAddress);
        setAppliedCoupons([]);
        setCouponInput("");
        setCustomizationsMap({});
        setBundleSelectionsMap({});
        setBundleSurchargeMap({});
        setPreview(null);
        setStep("products");
        onOpenChange(false);
        toast.success("Checkout link generated");
      } else {
        toast.error("Failed to generate checkout link");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to generate link";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [
    preview, address, storefrontTenantKey, conversationId,
    onCheckoutLinkReady, onOpenChange, emptyAddress,
    cart.clearCart, cart.items, // H3 fix: cart.items used for product image matching
    setGenerating, setAddress, setAppliedCoupons, setCouponInput,
    setCustomizationsMap, setBundleSelectionsMap, setBundleSurchargeMap,
    setPreview, setStep,
  ]);

  const handlePickerOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && cart.items.length > 0) {
        setStep("checkout");
      } else if (!newOpen) {
        onOpenChange(false);
      }
    },
    [cart.items.length, onOpenChange, setStep],
  );

  const handleCheckoutOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setStep("products");
        onOpenChange(false);
      }
    },
    [onOpenChange, setStep],
  );

  return {
    addItemToCart,
    handleSelectItem,
    handleBundleItemClick,
    handleBundleConfirm,
    handleRemoveItem,
    handleBackToProducts,
    updateAddress,
    callPreview,
    handlePreview,
    handleRemoveCoupon,
    handleGenerateLink,
    handlePickerOpenChange,
    handleCheckoutOpenChange,
  };
}
